<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { GRADIENT_PRESET_LIST } from '@/constants/gradientPresets'
import { useMockupEditorStore } from '@/stores/mockupEditor'
import { normalizeHex7, toCssLinearGradient } from '@/utils/gradient'

const store = useMockupEditorStore()
const { sortedGradientStops, activeGradientPresetId } = storeToRefs(store)

function onHexInput(stopId: string, e: Event): void {
  const el = e.target as HTMLInputElement
  store.setGradientStopHex(stopId, el.value)
}

function onPosInput(stopId: string, e: Event): void {
  const el = e.target as HTMLInputElement
  store.setGradientStopPosition(stopId, Number(el.value))
}

function onPickerInput(stopId: string, e: Event): void {
  const el = e.target as HTMLInputElement
  store.setGradientStopHex(stopId, el.value)
}
</script>

<template>
  <div class="grad">
    <div class="lbl2">{{ store.tUi('inspectorBackground.colorPresets') }}</div>
    <div class="presetGrid" role="list" :aria-label="store.tUi('inspectorGradient.templatesAria')">
      <button
        v-for="p in GRADIENT_PRESET_LIST"
        :key="p.id"
        type="button"
        class="preset"
        role="listitem"
        :style="{ background: toCssLinearGradient(p.stops) }"
        :aria-label="p.label ?? store.tUi('inspectorGradient.fallbackTemplateName', { id: p.id })"
        :aria-pressed="activeGradientPresetId === p.id ? 'true' : 'false'"
        :data-active="activeGradientPresetId === p.id ? 'true' : 'false'"
        @click="store.applyGradientPreset(p.id)"
      />
    </div>

    <div class="lbl2 rowHead">
      <span>{{ store.tUi('inspectorGradient.customGradient') }}</span>
    </div>

    <div class="stops" :aria-label="store.tUi('inspectorGradient.colorStopsAria')">
      <div v-for="s in sortedGradientStops" :key="s.id" class="stopRow">
        <input
          class="miniPicker"
          :value="normalizeHex7(s.hex)"
          type="color"
          :aria-label="store.tUi('inspectorGradient.stopColorAria', { position: Math.round(s.position) })"
          @input="onPickerInput(s.id, $event)"
        />
        <input
          class="hexInp"
          :value="s.hex"
          type="text"
          spellcheck="false"
          :aria-label="store.tUi('inspectorGradient.stopHexAria', { position: s.position })"
          @input="onHexInput(s.id, $event)"
        />
        <span class="pct mono">{{ Math.round(s.position) }}%</span>
        <input
          class="pos"
          type="range"
          min="0"
          max="100"
          step="0.1"
          :value="s.position"
          :aria-label="store.tUi('inspectorGradient.stopPositionAria', { hex: s.hex })"
          @input="onPosInput(s.id, $event)"
        />
        <button
          type="button"
          class="rm"
          :aria-label="store.tUi('inspectorGradient.removeStopAria', { hex: s.hex })"
          @click="store.removeGradientStop(s.id)"
        >
          ×
        </button>
      </div>
    </div>

    <button type="button" class="addBtn" @click="store.addGradientStop">
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" class="ico">
        <path
          fill="currentColor"
          d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5 16 5.67 16 6.5 15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9 19 9.67 19 10.5 18.33 12 17.5 12z"
        />
      </svg>
      {{ store.tUi('inspectorGradient.addColor') }}
    </button>
  </div>
</template>

<style scoped>
.grad {
  display: grid;
  gap: 0.65rem;
}

.lbl2 {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.rowHead {
  margin-top: 0.25rem;
}

.presetGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(2.75rem, 1fr));
  gap: 0.45rem;
}

/** 固定条高、横向随栅格伸缩，避免窄栏下比例块被压扁。 */
.preset {
  width: 100%;
  min-width: 0;
  height: 2.25rem;
  min-height: 2.25rem;
  border-radius: 8px;
  border: 1px solid var(--im-border);
  cursor: pointer;
  padding: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
  transition:
    transform 120ms ease,
    box-shadow 160ms ease;
}

.preset:hover {
  transform: translateY(-1px);
  box-shadow: var(--im-shadow);
}

.preset:focus-visible {
  outline: 2px solid #111;
  outline-offset: 2px;
}

.preset[data-active='true'] {
  outline: 2px solid #111;
  outline-offset: 0;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #111;
}

.stops {
  display: grid;
  gap: 0.5rem;
}

.stopRow {
  display: grid;
  grid-template-columns: 34px 1fr auto 1fr 34px;
  gap: 0.45rem;
  align-items: center;
}

@media (max-width: 360px) {
  .stopRow {
    grid-template-columns: 34px 1fr;
    grid-auto-rows: auto;
  }

  .pct,
  .pos,
  .rm {
    grid-column: 1 / -1;
  }
}

.miniPicker {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid var(--im-border);
  cursor: pointer;
}

.hexInp {
  border-radius: 8px;
  border: 1px solid var(--im-border);
  padding: 0.45rem 0.5rem;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.hexInp:focus-visible {
  border-color: #111;
  box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.08);
}

.pct {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-muted);
  text-align: right;
  min-width: 2.5rem;
}

.mono {
  font-variant-numeric: tabular-nums;
}

.pos {
  width: 100%;
  accent-color: #111;
}

.rm {
  border-radius: 8px;
  border: 1px solid var(--im-border);
  background: #fff;
  color: var(--im-muted);
  font-size: 1.1rem;
  line-height: 1;
  width: 34px;
  height: 34px;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.rm:hover {
  color: #111;
  background: var(--im-elevated);
}

.rm:focus-visible {
  outline: 2px solid #111;
  outline-offset: 2px;
}

.addBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: #fff;
  color: #111;
  font-weight: 850;
  font-size: 0.875rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
}

.addBtn:hover {
  background: var(--im-elevated);
}

.addBtn:focus-visible {
  outline: 2px solid #111;
  outline-offset: 2px;
}

.ico {
  flex-shrink: 0;
  opacity: 0.85;
}
</style>
